/**
 * OrganizationsPage — ADM-008
 *
 * Policy-aware page for viewing and managing organizations (LPU).
 * Features: table, create modal, edit drawer with capabilities toggles, deactivate.
 */
import { useState, useMemo, useCallback } from 'react'
import {
  Typography,
  Breadcrumb,
  Table,
  Button,
  Modal,
  Input,
  Space,
  Badge,
  Form,
  Popconfirm,
  Drawer,
  Switch,
  Divider,
  Descriptions,
  Alert,
  message,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  BankOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'

import { useOrgs, useCreateOrg, useUpdateOrg, useDeactivateOrg, useUpdateCapabilities } from '@/hooks/useOrgs'
import { useUsers } from '@/hooks/useUsers'
import { useAccessGroups } from '@/hooks/useAccessGroups'
import { useCapabilities } from '@/hooks/useCapabilities'
import type { OrgResponse } from '@/api/orgs'

dayjs.locale('ru')

const { Title, Text } = Typography
const { Search } = Input

// ── Capability labels (Russian) ──────────────────────────────

/** Dot-notation key → Russian label */
const CAPABILITY_LABELS: Record<string, string> = {
  'users.create': 'Создание сотрудников',
  'users.edit': 'Редактирование сотрудников',
  'users.deactivate': 'Деактивация сотрудников',
  'groups.manage': 'Управление группами доступа',
  'rls.manage': 'Управление RLS-привязками',
  'invites.create': 'Создание приглашений',
}

/** Dot-notation key → underscore key for API payload */
const CAPABILITY_API_KEY: Record<string, string> = {
  'users.create': 'users_create',
  'users.edit': 'users_edit',
  'users.deactivate': 'users_deactivate',
  'groups.manage': 'groups_manage',
  'rls.manage': 'rls_manage',
  'invites.create': 'invites_create',
}

const ALL_CAPABILITY_KEYS = Object.keys(CAPABILITY_LABELS)

/** Default capabilities — all enabled (matches backend DEFAULT_CAPABILITIES). */
const DEFAULT_CAPABILITIES: Record<string, boolean> = Object.fromEntries(
  ALL_CAPABILITY_KEYS.map((k) => [k, true]),
)

// ── Helpers ──────────────────────────────────────────────────

function getOrgCapabilities(org: OrgResponse): Record<string, boolean> {
  const caps = org.settings?.capabilities
  if (caps && typeof caps === 'object') {
    return { ...DEFAULT_CAPABILITIES, ...caps }
  }
  return { ...DEFAULT_CAPABILITIES }
}

// ── Component ────────────────────────────────────────────────

export default function OrganizationsPage() {
  const capabilities = useCapabilities()
  const canManageOrgs = capabilities.orgsManage

  // ── Search ──
  const [searchText, setSearchText] = useState('')

  // ── Create modal ──
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [createForm] = Form.useForm()

  // ── Edit drawer ──
  const [selectedOrg, setSelectedOrg] = useState<OrgResponse | null>(null)
  const [editForm] = Form.useForm()

  // ── Data hooks ──
  const { data: orgs, isLoading: orgsLoading } = useOrgs()
  const { data: usersData } = useUsers({ page: 1, page_size: 1000 })
  const { data: groups } = useAccessGroups()

  // ── Mutations ──
  const createOrgMutation = useCreateOrg()
  const updateOrgMutation = useUpdateOrg()
  const deactivateOrgMutation = useDeactivateOrg()
  const updateCapsMutation = useUpdateCapabilities()

  // ── Computed: counts per org ──
  const usersCountMap = useMemo(() => {
    const map: Record<string, number> = {}
    for (const u of usersData?.items || []) {
      if (u.org_id) {
        map[u.org_id] = (map[u.org_id] || 0) + 1
      }
    }
    return map
  }, [usersData])

  const groupsCountMap = useMemo(() => {
    const map: Record<string, number> = {}
    for (const g of groups || []) {
      if (g.org_id) {
        map[g.org_id] = (map[g.org_id] || 0) + 1
      }
    }
    return map
  }, [groups])

  // ── Filtered orgs ──
  const filteredOrgs = useMemo(() => {
    if (!orgs) return []
    if (!searchText) return orgs
    const lower = searchText.toLowerCase()
    return orgs.filter(
      (o) =>
        o.code.toLowerCase().includes(lower) ||
        o.name.toLowerCase().includes(lower) ||
        (o.short_name && o.short_name.toLowerCase().includes(lower)),
    )
  }, [orgs, searchText])

  // ── Handlers ──

  const handleCreate = async () => {
    if (!canManageOrgs) return
    try {
      const values = await createForm.validateFields()
      await createOrgMutation.mutateAsync(values)
      setCreateModalOpen(false)
      createForm.resetFields()
    } catch {
      // validation error — form shows inline messages
    }
  }

  const handleOpenEdit = useCallback(
    (org: OrgResponse) => {
      setSelectedOrg(org)
      editForm.setFieldsValue({
        name: org.name,
        short_name: org.short_name || '',
      })
    },
    [editForm],
  )

  const handleCloseEdit = () => {
    setSelectedOrg(null)
    editForm.resetFields()
  }

  const handleSaveEdit = async () => {
    if (!canManageOrgs) return
    if (!selectedOrg) return
    try {
      const values = await editForm.validateFields()
      const payload: Record<string, any> = {}
      if (values.name !== selectedOrg.name) payload.name = values.name
      if ((values.short_name || null) !== selectedOrg.short_name)
        payload.short_name = values.short_name || null

      if (Object.keys(payload).length > 0) {
        await updateOrgMutation.mutateAsync({ orgId: selectedOrg.id, data: payload })
        // Update local state to reflect changes in drawer
        setSelectedOrg((prev) => (prev ? { ...prev, ...payload } : prev))
      } else {
        message.info('Нет изменений для сохранения')
      }
    } catch {
      // validation error
    }
  }

  const handleDeactivate = async (orgId: string) => {
    if (!canManageOrgs) return
    await deactivateOrgMutation.mutateAsync(orgId)
    handleCloseEdit()
  }

  const handleCapabilityToggle = async (capKey: string, checked: boolean) => {
    if (!canManageOrgs) return
    if (!selectedOrg) return
    const apiKey = CAPABILITY_API_KEY[capKey]
    if (!apiKey) return

    try {
      await updateCapsMutation.mutateAsync({
        orgId: selectedOrg.id,
        data: { [apiKey]: checked },
      })
      // Update local state
      setSelectedOrg((prev) => {
        if (!prev) return prev
        const caps = getOrgCapabilities(prev)
        caps[capKey] = checked
        return {
          ...prev,
          settings: { ...prev.settings, capabilities: caps },
        }
      })
    } catch {
      // error handled by mutation onError
    }
  }

  // ── Table columns ──

  const columns: ColumnsType<OrgResponse> = [
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Краткое название',
      dataIndex: 'short_name',
      key: 'short_name',
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Сотрудники',
      key: 'users_count',
      width: 120,
      align: 'center',
      render: (_: any, record: OrgResponse) => usersCountMap[record.id] ?? 0,
      sorter: (a, b) => (usersCountMap[a.id] ?? 0) - (usersCountMap[b.id] ?? 0),
    },
    {
      title: 'Группы',
      key: 'groups_count',
      width: 100,
      align: 'center',
      render: (_: any, record: OrgResponse) => groupsCountMap[record.id] ?? 0,
      sorter: (a, b) => (groupsCountMap[a.id] ?? 0) - (groupsCountMap[b.id] ?? 0),
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (active: boolean) => (
        <Badge
          status={active ? 'success' : 'error'}
          text={active ? 'Активна' : 'Неактивна'}
        />
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: OrgResponse) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            title={canManageOrgs ? 'Редактировать' : 'Открыть карточку'}
            onClick={() => handleOpenEdit(record)}
          />
          {canManageOrgs && record.is_active && (
            <Popconfirm
              title="Деактивировать организацию?"
              description="Сотрудники потеряют доступ к платформе."
              onConfirm={() => handleDeactivate(record.id)}
              okText="Деактивировать"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" size="small" danger icon={<StopOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  // ── Capabilities for selected org ──
  const selectedCaps = selectedOrg ? getOrgCapabilities(selectedOrg) : DEFAULT_CAPABILITIES

  // ── Render ──

  return (
    <div>
      <Breadcrumb
        items={[
          { title: 'Администрирование' },
          { title: 'Организации' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Организации
        </Title>
        {canManageOrgs ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields()
              setCreateModalOpen(true)
            }}
          >
            Добавить ЛПУ
          </Button>
        ) : null}
      </div>

      {!canManageOrgs ? (
        <Alert
          showIcon
          type="info"
          message="Режим просмотра"
          description="Можно просматривать список организаций и настройки функций. Создание, изменение и деактивация доступны только пользователям с правом orgs.write."
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по коду или названию"
          allowClear
          onSearch={(v) => setSearchText(v)}
          onChange={(e) => {
            if (!e.target.value) setSearchText('')
          }}
          style={{ width: 300 }}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={filteredOrgs}
        rowKey="id"
        loading={orgsLoading}
        size="middle"
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
      />

      {/* ── Modal: Create Organization ── */}
      <Modal
        title="Добавить ЛПУ"
        open={createModalOpen}
        onOk={handleCreate}
        onCancel={() => setCreateModalOpen(false)}
        confirmLoading={createOrgMutation.isPending}
        okText="Создать"
        cancelText="Отмена"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="code"
            label="Код организации"
            rules={[
              { required: true, message: 'Введите код организации' },
              { max: 10, message: 'Максимум 10 символов' },
            ]}
          >
            <Input placeholder="150101" maxLength={10} />
          </Form.Item>
          <Form.Item
            name="name"
            label="Полное название"
            rules={[{ required: true, message: 'Введите название организации' }]}
          >
            <Input placeholder="ГБУЗ МО Королёвская городская больница" />
          </Form.Item>
          <Form.Item name="short_name" label="Краткое название">
            <Input placeholder="КГБ" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Drawer: Edit Organization ── */}
      <Drawer
        title={
          selectedOrg ? (
            <Space>
              <BankOutlined />
              <span>{selectedOrg.code} — {selectedOrg.name}</span>
            </Space>
          ) : (
            'Организация'
          )
        }
        placement="right"
        width={480}
        open={!!selectedOrg}
        onClose={handleCloseEdit}
      >
        {selectedOrg && (
          <>
            {/* Basic info */}
            <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Код">{selectedOrg.code}</Descriptions.Item>
              <Descriptions.Item label="Статус">
                <Badge
                  status={selectedOrg.is_active ? 'success' : 'error'}
                  text={selectedOrg.is_active ? 'Активна' : 'Неактивна'}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Создана">
                {dayjs(selectedOrg.created_at).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">
              {canManageOrgs ? 'Редактирование' : 'Реквизиты'}
            </Divider>

            <Form form={editForm} layout="vertical" disabled={!canManageOrgs}>
              <Form.Item
                name="name"
                label="Полное название"
                rules={[{ required: true, message: 'Введите название' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="short_name" label="Краткое название">
                <Input />
              </Form.Item>
              <Form.Item>
                {canManageOrgs ? (
                  <Button
                    type="primary"
                    onClick={handleSaveEdit}
                    loading={updateOrgMutation.isPending}
                  >
                    Сохранить
                  </Button>
                ) : null}
              </Form.Item>
            </Form>

            <Divider orientation="left">Настройки функций</Divider>

            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {canManageOrgs
                ? 'Управление доступными функциями для руководителей организации.'
                : 'Доступные функции для руководителей организации. Изменение недоступно в режиме просмотра.'}
            </Text>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {ALL_CAPABILITY_KEYS.map((capKey) => (
                <div
                  key={capKey}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text>{CAPABILITY_LABELS[capKey]}</Text>
                  <Switch
                    checked={selectedCaps[capKey] ?? true}
                    onChange={(checked) => handleCapabilityToggle(capKey, checked)}
                    loading={updateCapsMutation.isPending}
                    disabled={!canManageOrgs}
                    size="small"
                  />
                </div>
              ))}
            </div>

            {canManageOrgs && selectedOrg.is_active && (
              <>
                <Divider />
                <Popconfirm
                  title="Деактивировать организацию?"
                  description="Сотрудники организации потеряют доступ к платформе."
                  onConfirm={() => handleDeactivate(selectedOrg.id)}
                  okText="Деактивировать"
                  cancelText="Отмена"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    icon={<StopOutlined />}
                    loading={deactivateOrgMutation.isPending}
                    block
                  >
                    Деактивировать организацию
                  </Button>
                </Popconfirm>
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  )
}
